<script>
	import { onMount } from "svelte";
	let searchTerm = "";
	let searchResults = [];
	let selectedResult = null;
	let showingDetails = false;
	let selectedSeason = null;
	let selectedEpisode = null;
	let isLoading = false;
	let error = null;
	let hasSearched = false;
	let isSearchInputEmpty = false;
	const urlParams = new URLSearchParams(window.location.search);

	async function search() {
		if (searchTerm.trim() === "") {
			isSearchInputEmpty = true;
			return;
		}

		isSearchInputEmpty = false;
		isLoading = true;
		error = null;
		hasSearched = true;
		showingDetails = false;
		selectedResult = null;
		try {
			const response = await fetch(
				`https://cdn78.ok-master.fr.am/search_media?title=${encodeURIComponent(searchTerm)}`,
			);
			if (!response.ok) {
				throw new Error("خطا در دریافت اطلاعات از سرور");
			}
			searchResults = await response.json();
		} catch (err) {
			console.error("خطا در جستجو:", err);
			error = err.message;
			searchResults = [];
		} finally {
			isLoading = false;
		}
	}

	function showDetails(result) {
		selectedResult = result;
		showingDetails = true;
		selectedSeason = null;
		selectedEpisode = null;
	}

	function goBack() {
		showingDetails = false;
		selectedResult = null;
	}

	function selectSeason(season) {
		selectedSeason = season;
		selectedEpisode = null;
	}

	function selectEpisode(episode) {
		selectedEpisode = episode;
	}

	async function showFileId(file) {
		const userChatId = urlParams.get("user_chat_id");
		const user_session_id = urlParams.get("user_session_id");

		try {
			let data;
			if (selectedResult.type === "series") {
				data = {
					chatid: userChatId,
					user_session_id: user_session_id,
					oid: selectedResult._id.$oid,
					type: selectedResult.type,
					title: selectedResult.title,
					releaseYear: selectedResult.releaseYear,
					summary: selectedResult.summary,
					seasonNumber: selectedSeason?.seasonNumber,
					episodeNumber: selectedEpisode?.episodeNumber,
					duration: file.duration,
					quality: file.quality,
					more_details: file.more_details,
					vid_file_id: file.vid_file_id,
					encoder: file.encoder,
					fileSize: file.fileSize,
					average: selectedResult.rating.average,
					votes: selectedResult.rating.votes,
				};
			} else {
				data = {
					chatid: userChatId,
					user_session_id: user_session_id,
					oid: selectedResult._id.$oid,
					type: selectedResult.type,
					title: selectedResult.title,
					releaseYear: selectedResult.releaseYear,
					summary: selectedResult.summary,
					duration: selectedResult.duration,
					quality: file.quality,
					more_details: file.more_details,
					vid_file_id: file.vid_file_id,
					encoder: file.encoder,
					fileSize: file.fileSize,
					average: selectedResult.rating.average,
					votes: selectedResult.rating.votes,
				};
			}

			const response = await fetch(
				"https://cdn80.ok-master.fr.am/sendUserVideoInMiniApp",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"Access-Control-Allow-Methods": "POST",
						"Access-Control-Allow-Origin": "*",
						"Access-Control-Allow-Headers": "*",
					},
					body: JSON.stringify(data),
				},
			);

			if (!response.ok) {
				throw new Error("Error sending data to server");
			}

			const result = await response.json();
			window.Telegram.WebApp.showAlert(" فایل ارسال شد");
			console.log("Server response:", result);
		} catch (err) {
			console.error("Error sending data:", err);
		}
	}

	function formatFileSize(sizeInBytes) {
		if (typeof sizeInBytes !== "number") {
			return "نامشخص";
		}
		const sizeInMB = sizeInBytes / (1024 * 1024);
		if (sizeInMB < 1024) {
			return sizeInMB.toFixed(2) + " مگابایت";
		} else {
			const sizeInGB = sizeInMB / 1024;
			return sizeInGB.toFixed(2) + " گیگابایت";
		}
	}

	function formatDuration(durationInSeconds) {
		if (typeof durationInSeconds !== "number") {
			return "نامشخص";
		}
		const minutes = Math.floor(durationInSeconds / 60);
		return `${minutes} دقیقه`;
	}

	$: seasons =
		selectedResult?.type === "series" &&
		Array.isArray(selectedResult.seasons)
			? selectedResult.seasons.sort(
					(a, b) => a.seasonNumber - b.seasonNumber,
				)
			: [];
	$: episodes = selectedSeason?.episodes
		? selectedSeason.episodes.sort(
				(a, b) => a.episodeNumber - b.episodeNumber,
			)
		: [];
</script>

<main>
	<header>
		<form id="search-form" on:submit|preventDefault={search}>
			<div class="logo-container">
				<img src="telemovie_channel3.webp" alt="Telemovie logo" />
			</div>
			<div class="input-button-container">
				<input
					type="text"
					id="search-input"
					bind:value={searchTerm}
					placeholder="جستجوی فیلم و سریال..."
					class:empty={isSearchInputEmpty}
					on:input={() => (isSearchInputEmpty = false)}
				/>
				<button type="submit" id="search-button" disabled={isLoading}>
					{isLoading ? "در حال جستجو..." : "جستجو"}
				</button>
			</div>
		</form>
	</header>

	{#if error}
		<div class="error-message">{error}</div>
	{/if}

	{#if showingDetails && selectedResult}
		<div id="details-view">
			<div class="back-button-container">
				<button class="back-button" on:click={goBack}
					>← بازگشت به نتایج</button
				>
			</div>
			<h1>{selectedResult.title} ({selectedResult.releaseYear})</h1>
			<div class="summary">
				<h2>خلاصه:</h2>
				<p>{selectedResult.summary}</p>
			</div>

			{#if selectedResult.type === "series"}
				<div class="seasons">
					<h2>فصل‌ها:</h2>
					<div class="season-buttons">
						{#each seasons as season}
							<button
								class="season-button"
								class:active={selectedSeason === season}
								on:click={() => selectSeason(season)}
							>
								فصل {season.seasonNumber}
							</button>
						{/each}
					</div>
				</div>

				{#if selectedSeason}
					<div class="episodes">
						<h2>قسمت‌ها:</h2>
						<div class="episode-buttons">
							{#each episodes as episode}
								<button
									class="episode-button"
									class:active={selectedEpisode === episode}
									on:click={() => selectEpisode(episode)}
								>
									قسمت {episode.episodeNumber}
								</button>
							{/each}
						</div>
					</div>
				{/if}
			{/if}

			<div class="file-details">
				<h2>جزئیات فایل:</h2>
				{#if selectedResult.type === "movie"}
					{#if Array.isArray(selectedResult.downloadFiles)}
						{#each selectedResult.downloadFiles as file}
							<div class="file-item">
								<div class="file-info">
									<p>
										<strong>کیفیت:</strong>
										{file.quality || "نامشخص"}
									</p>
									<p>
										<strong>حجم فایل:</strong>
										{formatFileSize(
											file.fileSize?.$numberInt,
										)}
									</p>
									<p>
										<strong>مدت زمان:</strong>
										{formatDuration(
											file.duration?.$numberInt,
										)}
									</p>
									<p>
										<strong>انکودر:</strong>
										{file.encoder || "نامشخص"}
									</p>
									<p>
										<strong>جزئیات بیشتر:</strong>
										{file.more_details || "نامشخص"}
									</p>
								</div>
								<div class="file-id-button-container">
									<button on:click={() => showFileId(file)}
										>دریافت فایل در ربات</button
									>
								</div>
							</div>
						{/each}
					{:else}
						<p>اطلاعات فایل در دسترس نیست.</p>
					{/if}
				{:else if selectedResult.type === "series" && selectedEpisode}
					{#if Array.isArray(selectedEpisode.downloadFiles)}
						{#each selectedEpisode.downloadFiles as file}
							<div class="file-item">
								<div class="file-info">
									<p>
										<strong>کیفیت:</strong>
										{file.quality || "نامشخص"}
									</p>
									<p>
										<strong>حجم فایل:</strong>
										{formatFileSize(
											file.fileSize?.$numberInt,
										)}
									</p>
									<p>
										<strong>مدت زمان:</strong>
										{formatDuration(
											file.duration?.$numberInt,
										)}
									</p>
									<p>
										<strong>انکودر:</strong>
										{file.encoder || "نامشخص"}
									</p>
									<p>
										<strong>جزئیات بیشتر:</strong>
										{file.more_details || "نامشخص"}
									</p>
								</div>
								<div class="file-id-button-container">
									<button on:click={() => showFileId(file)}
										>دریافت فایل در ربات</button
									>
								</div>
							</div>
						{/each}
					{:else}
						<p>اطلاعات فایل در دسترس نیست.</p>
					{/if}
				{/if}
			</div>
		</div>
	{:else}
		<div id="search-results">
			{#if isLoading}
				<div class="loading">در حال بارگذاری...</div>
			{:else if searchResults.length > 0}
				{#each searchResults as result}
					<div
						class="result-item"
						on:click={() => showDetails(result)}
						on:keydown={(e) =>
							e.key === "Enter" && showDetails(result)}
						tabindex="0"
						role="button"
					>
						<div class="result-title">
							{result.title} ({result.releaseYear})
						</div>
						<div class="result-type">
							{result.type === "movie" ? "فیلم" : "سریال"}
						</div>
					</div>
				{/each}
			{:else if hasSearched}
				<div class="no-results">نتیجه‌ای یافت نشد.</div>
			{:else}
				<div class="initial-state">
					<img
						src="output-onlinegiftools.gif"
						alt="Cute animated heart"
					/>
				</div>
				<p class="initial-state">منتظریم یچیزی جستجو کنی...</p>
			{/if}
		</div>
	{/if}
</main>

<style>
	:global(body),
	:global(html) {
		margin: 0;
		padding: 0;
		font-family: Arial, sans-serif;
		background-color: #252729;
		color: #ffffff;
		direction: rtl;
	}

	main {
		max-width: 800px;
		margin: 0 auto;
		padding: 20px;
	}

	header {
		background-color: #1a1b1d;
		padding: 20px;
		margin-bottom: 20px;
		border-radius: 8px;
	}

	#search-form {
		display: flex;
		flex-direction: column;
		align-items: center;
	}

	.logo-container {
		margin-bottom: 10px;
	}

	.logo-container img {
		width: 100px;
		height: 53px;
	}

	.input-button-container {
		display: flex;
		width: 100%;
	}

	#search-input {
		flex: 1;
		padding: 12px;
		font-size: 16px;
		border: 2px solid transparent;
		border-radius: 24px;
		background-color: #3a3b3d;
		color: #ffffff;
		transition: all 0.3s ease;
	}

	#search-input::placeholder {
		color: #a0a0a0;
	}

	#search-input:focus {
		outline: none;
		border-color: #4285f4;
	}

	#search-input.empty {
		border-color: #ff6b6b;
		box-shadow: 0 0 0 2px rgba(255, 107, 107, 0.3);
	}

	#search-button {
		padding: 12px 20px;
		font-size: 16px;
		background-color: #4285f4;
		color: white;
		border: none;
		border-radius: 24px;
		cursor: pointer;
		transition: background-color 0.3s ease;
	}

	#search-button:hover {
		background-color: #3b78e7;
	}

	#search-button:disabled {
		background-color: #6c6c6c;
		cursor: not-allowed;
	}

	#details-view {
		background-color: #2d2e30;
		padding: 20px;
		border-radius: 8px;
		box-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
	}

	.back-button-container {
		justify-content: center;
		align-items: center;
		margin-bottom: 20px;
		position: relative;
	}

	.back-button {
		justify-content: center;
		align-items: center;
		background-color: #633336;
		border: none;
		padding: 10px 15px;
		border-radius: 4px;
		cursor: pointer;
		color: #ffffff;
		transition: background-color 0.3s ease;
	}

	.back-button:hover {
		background-color: #835356;
	}

	h1 {
		color: #4285f4;
		margin-bottom: 15px;
	}

	h2 {
		color: #ffffff;
		margin-top: 20px;
		margin-bottom: 10px;
	}

	.summary,
	.seasons,
	.episodes,
	.file-details {
		margin-bottom: 20px;
	}

	.season-buttons,
	.episode-buttons {
		display: flex;
		flex-wrap: wrap;
		gap: 10px;
	}

	.season-button,
	.episode-button {
		background-color: #3a3b3d;
		border: none;
		padding: 10px 15px;
		border-radius: 4px;
		cursor: pointer;
		color: #ffffff;
		transition: background-color 0.3s ease;
	}

	.season-button:hover,
	.episode-button:hover {
		background-color: #4a4b4d;
	}

	.season-button.active,
	.episode-button.active {
		background-color: #4285f4;
		color: white;
	}

	.file-item {
		background-color: #3a3b3d;
		padding: 15px;
		margin-bottom: 10px;
		border-radius: 4px;
		display: flex;
		flex-direction: column;
	}

	.file-info {
		flex: 1;
		margin-bottom: 10px;
	}

	.file-id-button-container {
		display: flex;
		justify-content: flex-start;
	}

	.file-id-button-container button {
		background-color: #4285f4;
		color: white;
		border: none;
		padding: 5px 10px;
		border-radius: 4px;
		cursor: pointer;
		transition: background-color 0.3s ease;
	}

	.file-id-button-container button:hover {
		background-color: #3b78e7;
	}

	#search-results {
		background-color: transparent;
	}

	.result-item {
		background-color: #2d2e30;
		margin-bottom: 10px;
		padding: 15px;
		border-radius: 8px;
		box-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
		cursor: pointer;
		transition: background-color 0.3s ease;
	}

	.result-item:hover {
		background-color: #3a3b3d;
	}

	.result-title {
		font-size: 18px;
		font-weight: bold;
		color: #4285f4;
		margin-bottom: 5px;
	}

	.result-type {
		font-size: 14px;
		color: #a0a0a0;
	}

	.no-results,
	.loading,
	.error-message {
		background-color: #2d2e30;
		padding: 20px;
		border-radius: 8px;
		box-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
		text-align: center;
		color: #ffffff;
		font-size: 16px;
	}

	.error-message {
		color: #ff6b6b;
	}

	.initial-state {
		display: flex;
		justify-content: center;
		align-items: center;
		height: 100px;
	}

	.initial-state img {
		max-width: 100%;
		max-height: 100%;
	}

	@media (max-width: 600px) {
		main {
			padding: 10px;
		}

		header {
			padding: 15px;
		}

		.input-button-container {
			flex-direction: column;
		}

		#search-input {
			border-radius: 24px;
			margin-bottom: 10px;
		}

		#search-button {
			border-radius: 24px;
			width: 100%;
		}

		.file-item {
			padding: 10px;
		}

		.result-item {
			padding: 10px;
		}
	}
</style>
